import React, { FunctionComponent, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { TabContext, TabPanel } from '@mui/lab';
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';

import ClassDetailsTab from './Tabs/ClassDetailsTab';
import StudentsTab from './Tabs/StudentsTab';
import TutorsTab from './Tabs/TutorsTab';
import VerticalTabBar, {
  IVerticalTabItem,
} from '../../../../../../../components/V2/Tabs/vertical/type1/VerticalTabBar';
import { useAppSelector } from '../../../../../../../hooks';
import {
  getLessonHistoryState,
  resetLessonHistoryData,
} from '../../../../../../../store/V2/LessonHistorySlice';
import BasicLoader from '../../../../../../../components/Loaders/BasicLoader';
import { getLessonHistoryById } from '../../../../../../../store/V2/LessonHistorySlice/api';
import FeedbacksTab from './Tabs/FeedbacksTab';
import { getAuthState } from '../../../../../../../store/AuthSlice';
import { checkUserPermission } from '../../../../../../../helper';
import { USER_PERMISSION_INDIVIDUALS } from '../../../../../../../store/V2/RolesSlice';
import TutorPaymentBreakdownTab from './Tabs/TutorPaymentBreakdownTab';
import { IUser } from '../../../../../../../store/UserSlice';

interface ViewLessonHistoryModalProps {
  open: boolean;
  onClose: () => void;
  lessonHistoryId: string;
  defaultTabIndex?: number;
}

export type TState = {
  selectedTab: IVerticalTabItem | null;
  tabs: IVerticalTabItem[];
};

const defaultTabs: IVerticalTabItem[] = [
  {
    index: 0,
    key: 'classDetails',
    title: 'Class Details',
  },
  {
    index: 1,
    key: 'feedbacks',
    title: 'Feedbacks',
  },
  {
    index: 2,
    key: 'tutorPaymentBreakdown',
    title: 'Payment Breakdown',
  },
  {
    index: 3,
    key: 'students',
    title: 'Students',
  },
  {
    index: 4,
    key: 'tutors',
    title: 'Tutors',
  },
];

const initialState: TState = {
  selectedTab: null,
  tabs: [],
};

const ViewLessonHistoryModal: FunctionComponent<ViewLessonHistoryModalProps> = ({
  open,
  onClose,
  lessonHistoryId,
  defaultTabIndex = 0,
}) => {
  const dispatch = useDispatch();

  // Store
  const lessonHistoryState = useAppSelector(getLessonHistoryState);

  // State
  const authState = useAppSelector(getAuthState);
  const [state, setState] = useState({ ...initialState });
  const [tabsUpdated, setTabsUpdated] = useState(false);

  const authUser = authState.user;
  const lessonHistory = lessonHistoryState.lessonHistoryData;
  const paymentTutorData =
    lessonHistory && lessonHistory.paymentTutor
      ? (lessonHistory.paymentTutor as IUser)
      : undefined;

  useEffect(() => {
    dispatch(getLessonHistoryById({ id: lessonHistoryId }));

    return () => {
      dispatch(resetLessonHistoryData());
    };
  }, []);

  useEffect(() => {
    if (!tabsUpdated && lessonHistory) {
      setTabs();
      setTabsUpdated(true);
    }
  }, [lessonHistory]);

  const updateStateAttributes = (attributes: Partial<TState>) => {
    setState((prevState) => ({
      ...prevState,
      ...attributes,
    }));
  };

  const setTabs = () => {
    const newTabs: IVerticalTabItem[] = [...defaultTabs]
      .filter((tabItem) => {
        if (tabItem.key === 'classDetails') {
          return checkUserPermission(authUser, {
            lessonHistoryCompletedTab: [
              USER_PERMISSION_INDIVIDUALS.LESSON_HISTORY_COMPLETED_TAB_DATE_TIME,
              USER_PERMISSION_INDIVIDUALS.LESSON_HISTORY_COMPLETED_TAB_CURRICULUM,
              USER_PERMISSION_INDIVIDUALS.LESSON_HISTORY_COMPLETED_TAB_SUBJECT,
              USER_PERMISSION_INDIVIDUALS.LESSON_HISTORY_COMPLETED_TAB_GRADE,
            ],
          });
        }

        if (tabItem.key === 'feedbacks') {
          return checkUserPermission(authUser, {
            lessonHistoryCompletedTab: [
              USER_PERMISSION_INDIVIDUALS.LESSON_HISTORY_COMPLETED_TAB_VIEW_FEEDBACKS,
            ],
          });
        }

        if (tabItem.key === 'tutorPaymentBreakdown') {
          return (
            checkUserPermission(authUser, {
              lessonHistoryCompletedTab: [
                USER_PERMISSION_INDIVIDUALS.LESSON_HISTORY_COMPLETED_TAB_TUTOR_PAYMENT_BREAKDOWN,
              ],
            }) &&
            authUser &&
            paymentTutorData &&
            authUser._id === paymentTutorData._id
          );
        }

        if (tabItem.key === 'students') {
          return checkUserPermission(authUser, {
            lessonHistoryCompletedTab: [
              USER_PERMISSION_INDIVIDUALS.LESSON_HISTORY_COMPLETED_TAB_STUDENTS,
            ],
          });
        }

        if (tabItem.key === 'tutors') {
          return checkUserPermission(authUser, {
            lessonHistoryCompletedTab: [
              USER_PERMISSION_INDIVIDUALS.LESSON_HISTORY_COMPLETED_TAB_TUTORS,
            ],
          });
        }

        return false;
      })
      .map((item, index) => ({ ...item, index }));

    let newSelectedTab: IVerticalTabItem | null = newTabs[0] || null;

    const defaultTabIndexItem = defaultTabs.find(
      (item) => item.index === defaultTabIndex
    );

    if (defaultTabIndexItem) {
      const matchingTab = newTabs.find(
        (item) => item.key === defaultTabIndexItem.key
      );

      if (matchingTab) {
        newSelectedTab = matchingTab;
      }
    }

    updateStateAttributes({
      tabs: newTabs,
      selectedTab: newSelectedTab,
    });
  };

  const renderTabPanel = () => {
    if (!lessonHistory) return null;

    let tabPanel: JSX.Element | null = null;

    if (state.selectedTab) {
      switch (state.selectedTab.key) {
        case 'classDetails':
          tabPanel = <ClassDetailsTab lessonHistory={lessonHistory} />;
          break;

        case 'feedbacks':
          tabPanel = <FeedbacksTab lessonHistory={lessonHistory} />;
          break;

        case 'tutorPaymentBreakdown':
          tabPanel = <TutorPaymentBreakdownTab lessonHistory={lessonHistory} />;
          break;

        case 'students':
          tabPanel = <StudentsTab lessonHistory={lessonHistory} />;
          break;

        case 'tutors':
          tabPanel = <TutorsTab lessonHistory={lessonHistory} />;
          break;

        default:
          tabPanel = null;
          break;
      }
    }

    return tabPanel;
  };

  return (
    <Dialog
      fullWidth
      maxWidth="lg"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: { borderRadius: '20px', maxWidth: '850px' },
      }}
    >
      <DialogTitle
        sx={{
          m: 0,
          p: { xs: '15px', sm: '30px' },
          color: '#000000',
          fontWeight: 600,
          fontSize: '16px',
        }}
      >
        More Details
      </DialogTitle>
      <IconButton
        aria-label="close"
        onClick={onClose}
        sx={{
          position: 'absolute',
          right: { xs: '7px', sm: '22px' },
          top: { xs: '7px', sm: '22px' },
        }}
      >
        <CloseIcon sx={{ color: '#000000' }} />
      </IconButton>
      <DialogContent
        sx={{ p: { xs: '0 15px 15px 15px', sm: '0 30px 30px 30px' } }}
      >
        {lessonHistoryState.status === 'loading' && !lessonHistory ? (
          <div style={{ padding: '16px 40px' }}>
            <BasicLoader />
          </div>
        ) : null}
        {lessonHistory ? (
          <div>
            <Grid container columnSpacing="24px" rowSpacing="16px">
              <Grid
                item
                xs={12}
                sm="auto"
                sx={{
                  width: { xs: '100%', sm: '200px' },
                  borderRight: '1px solid #CBD5E1',
                }}
              >
                <Box
                  sx={{
                    position: 'sticky',
                    top: 0,
                    minHeight: { xs: 'none', sm: '400px' },
                  }}
                >
                  <VerticalTabBar
                    selectedTab={state.selectedTab || undefined}
                    tabs={state.tabs}
                    onChange={(e, value) =>
                      updateStateAttributes({ selectedTab: state.tabs[value] })
                    }
                  />
                </Box>
              </Grid>
              <Grid item xs={12} sm>
                <div>{renderTabPanel()}</div>
              </Grid>
            </Grid>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
};

export default ViewLessonHistoryModal;
